/**
 * Role hierarchy and permission utilities for the golf application
 * Implements cumulative permission model where higher roles inherit lower role permissions
 *
 * Navigation structure supports 3 levels:
 * - Level 1: First-level nav items (shown in top bar and bottom bar)
 * - Level 2: Category headers in megamenu (italic serif, clickable)
 * - Level 3: Menu items under categories (ALL CAPS, small)
 */

import type {User} from '../../../types/auth'

type UserRole = 'SUPERUSER' | 'MANAGER' | 'CADDIEMASTER' | 'PLAYER'
type NavKey =
  | 'dashboard' | 'events' | 'competitions' | 'ranking' | 'friends' | 'stats' | 'profile'
  | 'administration' | 'settings' | 'scorecards' | 'users' | 'tenants' | 'reports'
  | 'calendar' | 'courseStats' | 'payments' | 'scheduleTemplates' | 'scorecardTemplates'
  | 'handicapRecommendations' | 'clubs' | 'courses' | 'other' | 'pointSystems' | 'series'
  | 'createEvent' | 'super' | 'templates' | 'analyticsAdmin' | 'emailLog' | 'categories'
  | 'addOns' | 'polls'

export interface NavigationItem {
  label: string
  path?: string
  key: NavKey
  role: UserRole
  children?: NavigationItem[]
  /** Whether this is a category header (level 2) - rendered differently in megamenu */
  isCategory?: boolean
  /** Whether this item should open a megamenu panel */
  hasMegamenu?: boolean
  /** Optional per-tenant feature flag on the current user; item is hidden unless user[featureFlag] is truthy */
  featureFlag?: keyof User
}

/** First-level items for bottom bar (simplified, no children) */
export interface BottomBarItem {
  key: NavKey
  path: string
  role: UserRole
}

type RoleHierarchyMap = Record<UserRole, UserRole[]>
type NavigationConfigMap = Record<UserRole, NavigationItem[]>
type BottomBarConfigMap = Record<UserRole, BottomBarItem[]>

// Role hierarchy definition - higher roles inherit all permissions of lower roles
const ROLE_HIERARCHY: RoleHierarchyMap = {
  SUPERUSER: ['SUPERUSER', 'MANAGER', 'CADDIEMASTER', 'PLAYER'],
  MANAGER: ['MANAGER', 'CADDIEMASTER', 'PLAYER'],
  CADDIEMASTER: ['CADDIEMASTER', 'PLAYER'],
  PLAYER: ['PLAYER']
} as const

// Bottom bar configuration - first level items only (for mobile/tablet portrait)
const BOTTOM_BAR_CONFIG: BottomBarConfigMap = {
  PLAYER: [
    { key: 'dashboard', path: '/dashboard', role: 'PLAYER' },
    { key: 'events', path: '/competitions', role: 'PLAYER' },
    { key: 'friends', path: '/friends', role: 'PLAYER' },
    { key: 'stats', path: '/stats', role: 'PLAYER' },
    { key: 'profile', path: '/profile', role: 'PLAYER' }
  ],
  CADDIEMASTER: [
    { key: 'dashboard', path: '/dashboard', role: 'CADDIEMASTER' },
    { key: 'competitions', path: '/competitions', role: 'CADDIEMASTER' },
    { key: 'profile', path: '/profile', role: 'CADDIEMASTER' }
  ],
  MANAGER: [
    { key: 'dashboard', path: '/dashboard', role: 'MANAGER' },
    { key: 'events', path: '/competitions', role: 'MANAGER' },
    { key: 'administration', path: '/administration', role: 'MANAGER' },
    { key: 'settings', path: '/settings', role: 'MANAGER' }
  ],
  SUPERUSER: [
    { key: 'dashboard', path: '/dashboard', role: 'SUPERUSER' },
    { key: 'competitions', path: '/competitions', role: 'SUPERUSER' },
    { key: 'administration', path: '/administration', role: 'SUPERUSER' },
    { key: 'settings', path: '/settings', role: 'SUPERUSER' }
  ]
} as const

// Full navigation configuration with megamenu structure
const NAVIGATION_CONFIG: NavigationConfigMap = {
  PLAYER: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      key: 'dashboard',
      role: 'PLAYER'
    },
    {
      label: 'Events',
      key: 'events',
      role: 'PLAYER',
      hasMegamenu: true,
      children: [
        {
          label: 'Events',
          path: '/competitions',
          key: 'competitions',
          role: 'PLAYER',
          isCategory: true,
          children: [
            {
              label: 'Calendar',
              path: '/competitions',
              key: 'calendar',
              role: 'PLAYER'
            }
          ]
        },
        {
          label: 'Ranking',
          path: '/player/rankings',
          key: 'ranking',
          role: 'PLAYER',
          isCategory: true,
          children: [
            {
              label: 'Series',
              path: '/player/rankings',
              key: 'series',
              role: 'PLAYER'
            }
          ]
        }
      ]
    },
    {
      label: 'Friends',
      path: '/friends',
      key: 'friends',
      role: 'PLAYER'
    },
    {
      label: 'Stats',
      path: '/stats',
      key: 'stats',
      role: 'PLAYER'
    },
    {
      label: 'Profile',
      path: '/profile',
      key: 'profile',
      role: 'PLAYER'
    }
  ],

  CADDIEMASTER: [
    { label: 'Dashboard', path: '/dashboard', key: 'dashboard', role: 'CADDIEMASTER' },
    { label: 'Competitions', path: '/competitions', key: 'competitions', role: 'CADDIEMASTER' },
    { label: 'Profile', path: '/profile', key: 'profile', role: 'CADDIEMASTER' }
  ],

  MANAGER: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      key: 'dashboard',
      role: 'MANAGER'
    },
    {
      label: 'Events',
      key: 'events',
      role: 'MANAGER',
      hasMegamenu: true,
      children: [
        {
          label: 'Events',
          path: '/competitions',
          key: 'competitions',
          role: 'MANAGER',
          isCategory: true,
          children: [
            {
              label: 'Calendar',
              path: '/competitions',
              key: 'calendar',
              role: 'MANAGER'
            },
            {
              label: 'Create Event',
              path: '/competitions/create',
              key: 'createEvent',
              role: 'MANAGER'
            },
            {
              label: 'Kategoriat',
              path: '/competitions/categories',
              key: 'categories',
              role: 'MANAGER'
            }
          ]
        },
        {
          label: 'Ranking',
          path: '/rankings',
          key: 'ranking',
          role: 'MANAGER',
          isCategory: true,
          children: [
            {
              label: 'Series',
              path: '/rankings',
              key: 'series',
              role: 'MANAGER'
            },
            {
              label: 'Point Systems',
              path: '/rankings/point-systems',
              key: 'pointSystems',
              role: 'MANAGER'
            }
          ]
        }
      ]
    },
    {
      label: 'Administration',
      path: '/administration',
      key: 'administration',
      role: 'MANAGER',
      hasMegamenu: true,
      children: [
        {
          label: 'Reports',
          path: '/reports',
          key: 'reports',
          role: 'MANAGER',
          isCategory: true,
          children: [
            {
              label: 'Course Stats',
              path: '/administration/analytics/course',
              key: 'courseStats',
              role: 'MANAGER'
            },
            {
              label: 'Payments',
              path: '/administration/payments',
              key: 'payments',
              role: 'MANAGER'
            }
          ]
        },
        {
          label: 'Add-on services',
          path: '/administration/add-ons',
          key: 'addOns',
          role: 'MANAGER',
          isCategory: true,
          featureFlag: 'addOnsEnabled'
        },
        {
          label: 'Course polls',
          path: '/administration/polls',
          key: 'polls',
          role: 'MANAGER',
          isCategory: true,
          featureFlag: 'pollsEnabled'
        }
      ]
    },
    {
      label: 'Settings',
      path: '/settings',
      key: 'settings',
      role: 'MANAGER',
      hasMegamenu: true,
      children: [
        {
          label: 'Pohjat',
          path: '/settings',
          key: 'templates',
          role: 'MANAGER',
          isCategory: true,
          children: [
            {
              label: 'Aikataulumallit',
              path: '/admin/time-schedule-templates',
              key: 'scheduleTemplates',
              role: 'MANAGER'
            },
            {
              label: 'Tuloskorttipohjat',
              path: '/admin/scorecards',
              key: 'scorecardTemplates',
              role: 'MANAGER'
            }
          ]
        }
      ]
    }
  ],

  SUPERUSER: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      key: 'dashboard',
      role: 'SUPERUSER'
    },
    {
      label: 'Competitions',
      key: 'competitions',
      role: 'SUPERUSER',
      hasMegamenu: true,
      children: [
        {
          label: 'Events',
          path: '/competitions',
          key: 'competitions',
          role: 'SUPERUSER',
          isCategory: true,
          children: [
            {
              label: 'Calendar',
              path: '/competitions',
              key: 'calendar',
              role: 'SUPERUSER'
            },
            {
              label: 'Create Event',
              path: '/competitions/create',
              key: 'createEvent',
              role: 'SUPERUSER'
            },
            {
              label: 'Kategoriat',
              path: '/competitions/categories',
              key: 'categories',
              role: 'SUPERUSER'
            }
          ]
        },
        {
          label: 'Ranking',
          path: '/rankings',
          key: 'ranking',
          role: 'SUPERUSER',
          isCategory: true,
          children: [
            {
              label: 'Series',
              path: '/rankings',
              key: 'series',
              role: 'SUPERUSER'
            },
            {
              label: 'Point Systems',
              path: '/rankings/point-systems',
              key: 'pointSystems',
              role: 'SUPERUSER'
            }
          ]
        }
      ]
    },
    {
      label: 'Administration',
      path: '/administration',
      key: 'administration',
      role: 'SUPERUSER',
      hasMegamenu: true,
      children: [
        {
          label: 'Reports',
          path: '/reports',
          key: 'reports',
          role: 'SUPERUSER',
          isCategory: true,
          children: [
            {
              label: 'Analytics',
              path: '/admin/analytics',
              key: 'analyticsAdmin',
              role: 'SUPERUSER'
            },
            {
              label: 'Course Stats',
              path: '/administration/analytics/course',
              key: 'courseStats',
              role: 'SUPERUSER'
            },
            {
              label: 'Payments',
              path: '/administration/payments',
              key: 'payments',
              role: 'SUPERUSER'
            }
          ]
        },
        {
          label: 'Super',
          path: '/administration',
          key: 'super',
          role: 'SUPERUSER',
          isCategory: true,
          children: [
            {
              label: 'Users',
              path: '/users',
              key: 'users',
              role: 'SUPERUSER'
            },
            {
              label: 'Clubs',
              path: '/tenants',
              key: 'tenants',
              role: 'SUPERUSER'
            },
            {
              label: 'Handicap Recommendations',
              path: '/administration/handicap-recommendations',
              key: 'handicapRecommendations',
              role: 'SUPERUSER'
            },
            {
              label: 'Email Log',
              path: '/administration/email-log',
              key: 'emailLog',
              role: 'SUPERUSER'
            }
          ]
        },
        {
          label: 'Add-on services',
          path: '/administration/add-ons',
          key: 'addOns',
          role: 'SUPERUSER',
          isCategory: true,
          featureFlag: 'addOnsEnabled'
        },
        {
          // No featureFlag: pollsEnabled is a tenant flag and a superuser carries no tenant, so
          // gating on it would permanently hide the entry from the only role that spans all clubs.
          label: 'Course polls',
          path: '/administration/polls',
          key: 'polls',
          role: 'SUPERUSER',
          isCategory: true
        }
      ]
    },
    {
      label: 'Settings',
      path: '/settings',
      key: 'settings',
      role: 'SUPERUSER',
      hasMegamenu: true,
      children: [
        {
          label: 'Pohjat',
          path: '/settings',
          key: 'templates',
          role: 'SUPERUSER',
          isCategory: true,
          children: [
            {
              label: 'Aikataulumallit',
              path: '/admin/time-schedule-templates',
              key: 'scheduleTemplates',
              role: 'SUPERUSER'
            },
            {
              label: 'Tuloskorttipohjat',
              path: '/admin/scorecards',
              key: 'scorecardTemplates',
              role: 'SUPERUSER'
            }
          ]
        }
      ]
    }
  ]
} as const

export const hasRoleAccess = (userRoles: UserRole[] | undefined, requiredRole: UserRole): boolean => {
  if (!userRoles || !requiredRole) return false

  return userRoles.some(role => ROLE_HIERARCHY[role]?.includes(requiredRole))
}

export const getHighestRole = (userRoles: UserRole[] | undefined): UserRole => {
  if (!userRoles || userRoles.length === 0) return 'PLAYER'

  const rolePriority: UserRole[] = ['SUPERUSER', 'MANAGER', 'CADDIEMASTER', 'PLAYER']

  for (const role of rolePriority) {
    if (userRoles.includes(role)) {
      return role
    }
  }

  return 'PLAYER'
}

export const getNavigationItems = (userRoles: UserRole[] | undefined): NavigationItem[] => {
  const primaryRole = getHighestRole(userRoles)
  const items = NAVIGATION_CONFIG[primaryRole]
  return items ? [...items] : [...NAVIGATION_CONFIG.PLAYER]
}

/** Recursively drop nav items whose feature flag is falsy on the current user (leaves flag-less items untouched). */
export const filterNavByFeatureFlags = (
  items: NavigationItem[],
  user: User | null | undefined
): NavigationItem[] => {
  return items
    .filter(item => !item.featureFlag || Boolean(user?.[item.featureFlag]))
    .map(item => (item.children
      ? {...item, children: filterNavByFeatureFlags(item.children, user)}
      : item))
}

export const getBottomBarItems = (userRoles: UserRole[] | undefined): BottomBarItem[] => {
  const primaryRole = getHighestRole(userRoles)
  const items = BOTTOM_BAR_CONFIG[primaryRole]
  return items ? [...items] : [...BOTTOM_BAR_CONFIG.PLAYER]
}

/** Check if any navigation item or its children match the current path */
export const isNavItemActive = (item: NavigationItem, currentPath: string): boolean => {
  if (item.path && currentPath.startsWith(item.path)) {
    return true
  }
  if (item.children) {
    return item.children.some(child => isNavItemActive(child, currentPath))
  }
  return false
}
